export const ACTIVE_CASE_SESSION_KEY = "isx_active_case_session_v1";
export const OFFLINE_QUEUE_STORAGE_KEY = "isx_offline_queue_v1";
export const UPLOAD_STORAGE_KEY = "isx_uploads_in_progress";

const APP_CASE_SESSION_IDS = ["app-case-session", "app-admin-case-session"];

const caseSessionRoot = () => {
  for (const id of APP_CASE_SESSION_IDS) {
    const root = document.getElementById(id);
    if (root) return root;
  }

  return null;
};

export const normalizeCaseCode = (caseCode) => {
  if (caseCode === undefined || caseCode === null) return "";
  return caseCode.toString().trim().toUpperCase();
};

export const readJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch (_error) {
    return fallback;
  }
};

export const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Storage can be unavailable in private browsing; server state remains authoritative.
  }
};

export const readActiveCaseSession = () => readJson(ACTIVE_CASE_SESSION_KEY, null);

export const writeActiveCaseSession = (session) => {
  writeJson(ACTIVE_CASE_SESSION_KEY, session);
};

export const normalizeUserId = (userId) => {
  if (userId === undefined || userId === null) return "";
  return userId.toString().trim();
};

const activeSessionCaseCode = () => normalizeCaseCode(readActiveCaseSession()?.caseCode);
const activeSessionUserId = () => normalizeUserId(readActiveCaseSession()?.userId);

export const activeCaseCode = (fallback = "") => {
  const root = caseSessionRoot();
  const caseCode = normalizeCaseCode(root?.dataset?.caseCode);
  if (caseCode) return caseCode;

  const fallbackCaseCode = normalizeCaseCode(fallback);
  if (fallbackCaseCode) return fallbackCaseCode;

  return root ? activeSessionCaseCode() : "";
};

export const activeUserId = (fallback = "") => {
  const root = caseSessionRoot();
  const userId = normalizeUserId(root?.dataset?.userId);
  if (userId) return userId;

  const fallbackUserId = normalizeUserId(fallback);
  if (fallbackUserId) return fallbackUserId;

  return root ? activeSessionUserId() : "";
};

export const itemCaseCode = (item) => {
  if (!item || typeof item !== "object") return "";

  return normalizeCaseCode(
    item.caseCode ??
      item.case_code ??
      item.case?.caseCode ??
      item.case?.case_code ??
      item.metadata?.caseCode ??
      item.metadata?.case_code ??
      item.payload?.caseCode ??
      item.payload?.case_code
  );
};

export const itemUserId = (item) => {
  if (!item || typeof item !== "object") return "";

  return normalizeUserId(
    item.userId ??
      item.user_id ??
      item.user?.id ??
      item.metadata?.userId ??
      item.metadata?.user_id ??
      item.payload?.userId ??
      item.payload?.user_id
  );
};

export const hasCaseScope = (item) => itemCaseCode(item) !== "";
export const hasUserScope = (item) => itemUserId(item) !== "";

export const withCaseScope = (item, caseCode, userId = activeUserId()) => {
  const normalizedCaseCode = normalizeCaseCode(caseCode) || activeCaseCode();
  const normalizedUserId = normalizeUserId(userId) || activeUserId();

  if (!normalizedCaseCode) {
    return item;
  }

  const scopedItem = itemCaseCode(item) ? { ...item } : { ...item, caseCode: normalizedCaseCode };

  if (normalizedUserId && !itemUserId(scopedItem)) {
    return { ...scopedItem, userId: normalizedUserId };
  }

  return scopedItem;
};

export const filterItemsForCase = (items, caseCode, userId = activeUserId()) => {
  const queue = Array.isArray(items) ? items : [];
  const normalizedCaseCode = normalizeCaseCode(caseCode);
  const normalizedUserId = normalizeUserId(userId);

  if (!normalizedCaseCode) {
    return queue;
  }

  return queue.filter((item) => {
    if (itemCaseCode(item) !== normalizedCaseCode) return false;
    if (!normalizedUserId) return true;
    return itemUserId(item) === normalizedUserId;
  });
};

export const scopedItemsOnly = (items) => {
  const queue = Array.isArray(items) ? items : [];
  return queue.filter((item) => hasCaseScope(item) && hasUserScope(item));
};

export const readItemsForCase = (key, caseCode, userId = activeUserId()) => {
  const root = caseSessionRoot();
  const normalizedCaseCode = normalizeCaseCode(caseCode) || (root ? activeSessionCaseCode() : "");
  const normalizedUserId = normalizeUserId(userId);

  if (!normalizedCaseCode && root) {
    return [];
  }

  return filterItemsForCase(readJson(key, []), normalizedCaseCode, normalizedUserId);
};

export const dispatchScopedStorageUpdate = (eventName, key, caseCode, userId = activeUserId()) => {
  window.dispatchEvent(
    new CustomEvent(eventName, { detail: readItemsForCase(key, caseCode, userId) })
  );
};

const leadDraftKeysForCase = ({ userId, caseCode } = {}) => {
  const rawCaseCode =
    caseCode === undefined || caseCode === null ? "" : caseCode.toString().trim();
  const normalizedCaseCode = normalizeCaseCode(caseCode);
  const caseCodeVariants = Array.from(
    new Set([rawCaseCode, normalizedCaseCode].filter(Boolean))
  );
  const keys = [];

  if (userId) {
    caseCodeVariants.forEach((caseCodeVariant) => {
      keys.push(`lead_draft:${userId}:${caseCodeVariant}`);
    });
  } else if (caseCodeVariants.length > 0) {
    caseCodeVariants.forEach((caseCodeVariant) => {
      keys.push(`isx_lead_draft_${caseCodeVariant}`);
    });
  } else {
    keys.push("isx_lead_draft_unknown");
  }

  return keys;
};

export const readLeadDraftForCase = ({ userId, caseCode } = {}) => {
  let newestDraft = null;
  let newestTimestamp = Number.NEGATIVE_INFINITY;

  for (const key of leadDraftKeysForCase({ userId, caseCode })) {
    const draft = readJson(key, null);
    const savedAt = draft?.saved_at || draft?.savedAt;
    const savedAtTimestamp = Date.parse(savedAt);

    if (draft && savedAt && savedAtTimestamp > newestTimestamp) {
      newestDraft = { ...draft, savedAt, storageKey: key };
      newestTimestamp = savedAtTimestamp;
    }
  }

  return newestDraft;
};

export const pathWithCaseCode = (path, caseCode) => {
  const normalizedCaseCode = normalizeCaseCode(caseCode);

  if (!normalizedCaseCode) {
    return path;
  }

  const url = new URL(path, window.location.origin);
  url.searchParams.set("case_code", normalizedCaseCode);
  return `${url.pathname}${url.search}${url.hash}`;
};
